/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Article Tile PD component — single editorial card for the Blog page grid
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';

/* v8 ignore start */
@Component('articleTile', {
    name: 'Article Tile',
    description: 'Single editorial card for blog/article grids. Image + eyebrow + title + body copy + CTA.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ArticleTileMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'body', name: 'Body Copy', type: 'text' })
    body?: string;

    @AttributeDefinition({ id: 'ctaText', name: 'CTA Text', type: 'string' })
    ctaText?: string;

    @AttributeDefinition({ id: 'ctaLink', name: 'CTA Link', type: 'url' })
    ctaLink?: string;
}
/* v8 ignore stop */

export default function ArticleTile({
    image,
    imageAlt,
    eyebrow,
    title,
    body,
    ctaText,
    ctaLink,
}: {
    image?: Image;
    imageAlt?: string;
    eyebrow?: string;
    title?: string;
    body?: string;
    ctaText?: string;
    ctaLink?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-60px' });

    return (
        <article ref={ref} className={cn('flex flex-col df-reveal', inView && 'df-reveal--show')}>
            {image?.url && (
                <div className="relative overflow-hidden rounded-ui aspect-square bg-muted mb-6">
                    <DynamicImage
                        src={image.url}
                        alt={imageAlt ?? ''}
                        widths={{ base: 768, lg: 720 }}
                        className="absolute inset-0 w-full h-full"
                        imageProps={{
                            className: 'w-full h-full object-cover transition-transform duration-700 hover:scale-105',
                        }}
                    />
                    {/* RGBARRY-CUSTOMIZATION: stretched link makes the image clickable to the same
                        destination as the tile's CTA, which remains independently clickable below */}
                    {ctaText && ctaLink && <StretchedLink to={ctaLink} />}
                </div>
            )}
            <div className="flex flex-col gap-4 px-1">
                {eyebrow && <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{eyebrow}</p>}
                <h3
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-2xl lg:text-3xl font-light leading-snug text-foreground min-h-[2lh]">
                    {title ?? ''}
                </h3>
                {body && <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>}
                {ctaText && ctaLink && (
                    <Button
                        asChild
                        className="df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 self-start">
                        <Link to={ctaLink}>{ctaText}</Link>
                    </Button>
                )}
            </div>
        </article>
    );
}
